import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { freeToolsAPI } from '../services/api';
import { CreditCounter, PaymentModal } from './CreditComponents';
import { useAuth } from '../contexts/AuthContext';
import logo from '../assets/logo.png';

const CoverLetterStarter = () => {
  const { isAuthenticated, user } = useAuth();
  
  // Form state
  const [jobDescription, setJobDescription] = useState('');
  const [resumeInputMethod, setResumeInputMethod] = useState('text'); // 'text' or 'file'
  const [resumeText, setResumeText] = useState('');
  const [resumeFile, setResumeFile] = useState(null);
  const [companyContext, setCompanyContext] = useState('');
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!validTypes.includes(file.type) && !file.name.match(/\.(pdf|docx)$/i)) {
        setError('Please upload a PDF or DOCX file');
        return;
      }
      
      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB');
        return;
      }
      
      setResumeFile(file);
      setError(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setResult(null);
    
    // Validation
    if (jobDescription.length < 200) {
      setError('Job description must be at least 200 characters for accurate matching');
      return;
    }
    
    if (resumeInputMethod === 'text' && resumeText.length < 50) {
      setError('Resume text must be at least 50 characters');
      return;
    }
    
    if (resumeInputMethod === 'file' && !resumeFile) {
      setError('Please upload your resume file');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await freeToolsAPI.coverLetterStarter(
        jobDescription,
        resumeInputMethod === 'text' ? resumeText : null,
        resumeInputMethod === 'file' ? resumeFile : null,
        companyContext.trim() || null
      );
      
      setResult(response);
      
      // Track analytics
      freeToolsAPI.trackAnalyticsEvent('cover-starter_completed', {
        used_company_context: response.used_company_context,
        word_count: response.word_count
      }).catch(() => {});
      
    } catch (err) {
      console.error('Cover Letter generation failed:', err);
      
      // Handle 402 Payment Required - Show payment modal
      if (err.response?.status === 402) {
        setShowPaymentModal(true);
        return;
      }
      
      if (err.response?.status === 429) {
        setError('Daily limit reached. Sign up for unlimited cover letters!');
      } else if (err.response?.status === 503) {
        setError('Cover Letter Starter is temporarily unavailable. Please try again later.');
      } else if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        if (typeof detail === 'object' && detail.error === 'payment_required') {
          setShowPaymentModal(true);
          return;
        }
        setError(typeof detail === 'string' ? detail : 'Failed to generate intro. Please try again.');
      } else {
        setError('Failed to generate intro. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCopyToClipboard = async () => {
    if (result?.cover_letter) {
      try {
        await navigator.clipboard.writeText(result.cover_letter);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  const handleReset = () => {
    setResult(null);
    setError(null);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation - Matching Home.jsx */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/" className="flex items-center gap-3">
              <img src={logo} alt="AlignifyNOW" className="h-[80px] w-auto" />
              <span className="text-xl font-bold tracking-tight text-slate-900 hidden sm:inline">
                Alignify<span className="text-blue-600">NOW</span>
              </span>
            </Link>
            <div className="flex items-center space-x-4">
              <Link to="/" className="text-sm text-gray-600 hover:text-gray-900 hidden sm:block">
                Match Score
              </Link>
              <Link to="/tracker" className="text-sm text-gray-600 hover:text-gray-900 hidden sm:block">
                Job Tracker
              </Link>
              <Link to="/blog" className="text-sm text-gray-600 hover:text-gray-900 hidden sm:block">
                Blog
              </Link>
              <Link to="/checklist" className="text-sm text-gray-600 hover:text-gray-900 hidden sm:block">
                Free Checklist
              </Link>
              <Link to="/pricing" className="text-sm text-gray-600 hover:text-gray-900 hidden sm:block">
                Pricing
              </Link>
              {/* Credit Counter - Shows for authenticated users */}
              <CreditCounter className="hidden sm:flex" />
              {isAuthenticated ? (
                <Link
                  to="/tracker"
                  className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-200 transition-colors"
                >
                  {user?.picture ? (
                    <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs">
                      {user?.name?.charAt(0) || 'U'}
                    </div>
                  )}
                  <span className="hidden md:inline">{user?.name?.split(' ')[0] || 'Dashboard'}</span>
                </Link>
              ) : (
                <Link
                  to="/tracker"
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Sign In
                </Link>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section with Background */}
      <section className="relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?auto=format&fit=crop&w=2000&q=80')" }}
        ></div>
        <div className="absolute inset-0 bg-slate-900/85"></div>
        
        <div className="relative max-w-4xl mx-auto px-4 py-16 lg:py-20 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight tracking-tight mb-4">
            Cover Letter{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">
              Starter
            </span>
          </h1>
          <p className="text-lg sm:text-xl text-gray-300 max-w-2xl mx-auto">
            Get a personalized opening hook that grabs attention—in seconds.
          </p>
          
          {/* Trust Badge */}
          <div className="mt-6 inline-flex items-center px-5 py-3 bg-green-600 text-white font-bold rounded-full shadow-lg border-2 border-green-400">
            <span className="text-xl mr-2">✅</span>
            <span className="text-base">Zero-Retention Guarantee</span>
          </div>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 py-12">
        {!result ? (
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 md:p-8">
            {/* Error Display */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <span className="text-red-800 text-sm">{error}</span>
                </div>
              </div>
            )}

            {/* Job Description */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Job Description <span className="text-red-500">*</span>
              </label>
              <textarea
                value={jobDescription}
                onChange={(e) => setJobDescription(e.target.value)}
                placeholder="Paste the full job description here... (minimum 200 characters)"
                rows={5}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none text-sm"
                required
              />
              <p className="text-xs text-gray-400 mt-1 text-right">
                {jobDescription.length} / 200 min
              </p>
            </div>

            {/* Resume Input Method Toggle */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Your Resume <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <button
                  type="button"
                  onClick={() => setResumeInputMethod('text')}
                  className={`p-3 rounded-lg border-2 transition-all text-sm font-medium ${
                    resumeInputMethod === 'text'
                      ? 'border-purple-500 bg-purple-50 text-purple-700'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  Paste Text
                </button>
                
                <button
                  type="button"
                  onClick={() => setResumeInputMethod('file')}
                  className={`p-3 rounded-lg border-2 transition-all text-sm font-medium ${
                    resumeInputMethod === 'file'
                      ? 'border-purple-500 bg-purple-50 text-purple-700'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  Upload File
                </button>
              </div>

              {resumeInputMethod === 'text' ? (
                <textarea
                  value={resumeText}
                  onChange={(e) => setResumeText(e.target.value)}
                  placeholder="Paste your resume content here... (minimum 50 characters)"
                  rows={5}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none text-sm"
                />
              ) : (
                <label htmlFor="resume-file-cl" className="block border-2 border-dashed border-gray-200 rounded-lg p-8 text-center hover:border-purple-400 transition-colors cursor-pointer">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.docx"
                    onChange={handleFileChange}
                    className="hidden"
                    id="resume-file-cl"
                  />
                  {resumeFile ? (
                    <div className="flex items-center justify-center">
                      <svg className="w-8 h-8 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div className="text-left">
                        <p className="font-medium text-gray-900">{resumeFile.name}</p>
                        <p className="text-sm text-gray-500">{(resumeFile.size / 1024).toFixed(1)} KB</p>
                      </div>
                    </div>
                  ) : (
                    <>
                      <svg className="w-12 h-12 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      <p className="text-gray-600 font-medium">Click to upload your resume</p>
                      <p className="text-sm text-gray-500 mt-1">PDF or DOCX, max 5MB</p>
                    </>
                  )}
                </label>
              )}
            </div>

            {/* Secret Weapon - Company Context */}
            <div className="mb-6 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-5 border border-amber-200">
              <div className="flex items-start mb-3">
                <span className="text-2xl mr-3">🎯</span>
                <div>
                  <label className="block text-sm font-semibold text-gray-900">
                    Your Secret Weapon <span className="text-gray-500 font-normal">(Optional)</span>
                  </label>
                  <p className="text-xs text-gray-600 mt-1">
                    Share why you&apos;re excited about this company. We&apos;ll weave it into your opening hook.
                  </p>
                </div>
              </div>
              <textarea
                value={companyContext}
                onChange={(e) => setCompanyContext(e.target.value)}
                placeholder="Examples:&#10;• They just raised Series B and are scaling their AI team&#10;• I love their remote-first culture&#10;• Their recent product launch aligns with my passion"
                rows={3}
                className="w-full px-4 py-3 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none bg-white text-sm"
              />
            </div>

            {/* Privacy Notice */}
            <div className="mb-6 text-center">
              <p className="text-xs text-gray-500">
                🔒 Zero-retention: Your files are processed in memory and never stored.{' '}
                <Link to="/privacy" className="text-purple-600 hover:underline">
                  Privacy Policy
                </Link>
              </p>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 px-6 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center shadow-lg"
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Generating Your Intro...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Generate Free Intro
                </>
              )}
            </button>
          </form>
        ) : (
          /* Results View */
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            {/* Results Header */}
            <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-6 py-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mr-4">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Your Opening Hook</h2>
                    <p className="text-purple-100 text-sm">
                      {result.used_company_context ? '✨ Personalized with your context' : 'Skills-focused opener'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleReset}
                  className="px-4 py-2 bg-white/20 text-white rounded-lg hover:bg-white/30 transition-colors text-sm font-medium"
                >
                  Start Over
                </button>
              </div>
            </div>

            {/* Free Intro Content */}
            <div className="p-6 md:p-8">
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 mb-6 border border-purple-100">
                <div className="flex items-center mb-3">
                  <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-full">FREE INTRO</span>
                </div>
                <div className="prose prose-gray max-w-none">
                  <p className="text-gray-800 leading-relaxed text-base whitespace-pre-wrap">
                    {result.cover_letter}
                  </p>
                </div>
              </div>

              {/* Copy Button */}
              <button
                onClick={handleCopyToClipboard}
                className="w-full py-3 px-4 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center mb-6"
              >
                {copied ? (
                  <>
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    Copied!
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    Copy to Clipboard
                  </>
                )}
              </button>

              {/* Blurred Body Preview - UPSELL */}
              <div className="relative rounded-xl overflow-hidden mb-6">
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/70 to-white z-10"></div>
                <div className="blur-sm select-none p-6 bg-gray-50 border border-gray-200 rounded-xl">
                  <p className="text-gray-600 text-sm leading-relaxed mb-4">
                    In my current role at [Company], I have successfully led cross-functional teams to deliver projects that increased revenue by 40%. My expertise in Python, React, and cloud technologies directly aligns with your requirements...
                  </p>
                  <p className="text-gray-600 text-sm leading-relaxed mb-4">
                    What excites me most about this opportunity is the chance to contribute to your mission of [mission]. I am particularly drawn to your innovative approach to [specific area]...
                  </p>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    I would welcome the opportunity to discuss how my background and skills would be a strong fit for your team. Thank you for considering my application...
                  </p>
                </div>
                
                {/* Unlock CTA Overlay */}
                <div className="absolute inset-0 flex items-center justify-center z-20">
                  <div className="text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
                      <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Full Letter Locked</h3>
                    <p className="text-sm text-gray-600 mb-4 max-w-xs">
                      Get the complete body paragraphs, closing, and unlimited rewrites.
                    </p>
                    <button className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg">
                      🔓 Unlock Full Letter & Rewrite (Pro)
                    </button>
                  </div>
                </div>
              </div>

              {/* Upsell Message */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-5 border border-purple-200">
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                    <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Want the Full Letter?</h3>
                    <p className="text-gray-600 text-sm">{result.upsell_hook}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Feature Highlights */}
        {!result && (
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">AI-Powered Hook</h3>
              <p className="text-sm text-gray-600">Get a compelling opening paragraph tailored to your experience and the role.</p>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Zero Retention</h3>
              <p className="text-sm text-gray-600">Your resume is processed in memory only. We never store your documents.</p>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Secret Weapon</h3>
              <p className="text-sm text-gray-600">Add company context for a personalized hook that gets noticed.</p>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center mb-4 md:mb-0">
              <span className="text-xl font-bold text-white opacity-80">
                Alignify<span className="text-blue-400">NOW</span>
              </span>
            </div>
            <div className="text-gray-400 text-xs mb-4 md:mb-0">
              © 2026 AlignifyNOW. All rights reserved. Built by Matambanadzo Studio.
            </div>
            <div className="flex items-center space-x-6">
              <Link to="/privacy" className="text-gray-400 hover:text-white text-sm transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-gray-400 hover:text-white text-sm transition-colors">
                Terms of Service
              </Link>
              <Link to="/blog" className="text-gray-400 hover:text-white text-sm transition-colors">
                Blog
              </Link>
              <Link to="/affiliates" className="text-gray-400 hover:text-white text-sm transition-colors">
                Affiliates
              </Link>
              <Link to="/checklist" className="text-gray-400 hover:text-white text-sm transition-colors">
                Free Checklist
              </Link>
            </div>
          </div>
        </div>
      </footer>

      {/* Payment Modal */}
      <PaymentModal 
        isOpen={showPaymentModal} 
        onClose={() => setShowPaymentModal(false)} 
      />
    </div>
  );
};

export default CoverLetterStarter;
